import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Loader2, Check, MapPin, Phone } from "lucide-react";
import { api, API, rp, fmtTime } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const STAGES = [
  ["delivery_before", "Before Delivery"],
  ["delivery_after", "After Delivery"],
  ["pickup_before", "Before Pickup"],
  ["pickup_after", "After Pickup"],
];

export default function Capture() {
  const { user } = useAuth();
  const isDriver = user?.role === "driver";
  const [day, setDay] = useState(new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    api.get("/bookings", { params: { date_from: day, date_to: day } }).then((r) =>
      setRows(r.data.filter((b) => b.booking_status !== "Cancelled")));
  }, [day]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-capture" className="max-w-2xl">
      <h1 className="font-display text-3xl tracking-tight">Driver Capture</h1>
      <p className="mt-1 text-sm text-ink-soft">Snap before &amp; after photos or videos at each stop.</p>
      {isDriver && (
        <p className="mt-2 text-xs text-amber-700" data-testid="capture-driver-scope">
          Showing only the stops assigned to you.
        </p>
      )}

      <div className="mt-4 flex items-center gap-2">
        <input data-testid="capture-date" type="date" value={day} onChange={(e) => setDay(e.target.value)}
          className="h-11 rounded-md border border-input bg-white px-3 text-sm" />
        <button onClick={() => setDay(new Date().toISOString().slice(0, 10))}
          className="h-11 px-4 rounded-md border border-black/15 text-sm">Today</button>
      </div>

      <div className="mt-4 space-y-3">
        {rows.map((b) => (
          <div key={b.id} className="bg-white border border-black/10 rounded-lg p-4" data-testid={`capture-booking-${b.booking_code}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-sm">{b.customer_name}</div>
                <div className="text-xs text-ink-soft">{b.booking_code} · {b.start_time}–{fmtTime(b.end_datetime)} · {b.quantity} unit · {b.vehicle_type === "bike" ? "Bike" : b.vehicle_type === "car" ? "Car" : "Self Pickup"}</div>
                <div className="text-xs text-ink-soft mt-1">{b.delivery_type === "delivery" ? `${b.address || ""} ${b.area ? `· ${b.area}` : ""}` : "Self pickup at HQ"}</div>
                {b.location_notes && <div className="text-xs text-amber-700 mt-1">{b.location_notes}</div>}
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-semibold">{rp(b.total_payment)}</div>
                <div className="text-[11px] text-ink-soft">{b.payment_status}</div>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <a href={`tel:${b.customer_whatsapp}`} className="text-xs px-3 py-2 rounded-full border border-black/15 inline-flex items-center gap-1"><Phone size={12} /> Call</a>
              {b.maps_link && <a href={b.maps_link} target="_blank" rel="noreferrer" className="text-xs px-3 py-2 rounded-full border border-black/15 inline-flex items-center gap-1"><MapPin size={12} /> Maps</a>}
              <button data-testid={`capture-open-${b.booking_code}`} onClick={() => setOpen(open === b.id ? null : b.id)}
                className="text-xs px-3 py-2 rounded-full lm-btn-primary inline-flex items-center gap-1"><Camera size={12} /> Capture</button>
            </div>
            {open === b.id && <CaptureBox bookingId={b.id} />}
          </div>
        ))}
        {rows.length === 0 && (
          <div className="bg-white border border-black/10 rounded-lg p-5 text-sm text-ink-soft" data-testid="capture-empty">
            {isDriver ? "No stops assigned to you on this date." : "No rentals on this date."}
          </div>
        )}
      </div>
    </div>
  );
}

const CaptureBox = ({ bookingId }) => {
  const [items, setItems] = useState([]);
  const [stage, setStage] = useState("delivery_before");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(() => api.get("/media", { params: { booking_id: bookingId } }).then((r) => setItems(r.data)), [bookingId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("booking_id", bookingId);
        fd.append("stage", stage);
        fd.append("notes", "");
        fd.append("file", f);
        await api.post("/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      if (inputRef.current) inputRef.current.value = "";
      load(); toast.success("Uploaded");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-4 border-t border-black/5 pt-3" data-testid="capture-box">
      <div className="flex flex-wrap gap-2">
        {STAGES.map(([v, l]) => {
          const n = items.filter((m) => m.stage === v).length;
          return (
            <button key={v} data-testid={`capture-stage-${v}`} onClick={() => setStage(v)}
              className={`text-xs px-3 py-2 rounded-full border inline-flex items-center gap-1 ${stage === v ? "bg-ink text-cream border-ink" : "border-black/15"}`}>
              {n > 0 && <Check size={11} />}{l} ({n})
            </button>
          );
        })}
      </div>
      <label className={`mt-3 flex items-center justify-center gap-2 rounded-md py-4 text-sm border-2 border-dashed border-black/15 cursor-pointer ${busy ? "opacity-50" : ""}`}>
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />} Take / choose photo or video
        <input ref={inputRef} data-testid="capture-input" type="file" accept="image/*,video/*" capture="environment" multiple hidden disabled={busy} onChange={upload} />
      </label>
      <div className="mt-3 flex flex-wrap gap-2">
        {items.filter((m) => m.stage === stage).map((m) => (
          <a key={m.id} href={`${API}${m.url.replace("/api", "")}`} target="_blank" rel="noreferrer">
            {m.media_type === "video"
              ? <video src={`${API}${m.url.replace("/api", "")}`} className="w-20 h-16 object-cover rounded border border-black/10" muted />
              : <img src={`${API}${m.url.replace("/api", "")}`} alt="" className="w-20 h-16 object-cover rounded border border-black/10" />}
          </a>
        ))}
      </div>
    </div>
  );
};
